import type {
  OrderDateRangePreset,
  OrderFulfillmentGroup,
  OrderStatusFilter,
  PosOrderSource,
} from '../../types'

export const FULFILLMENT_GROUP: Record<string, OrderFulfillmentGroup> = {
  cantec_local_pickup: 'pickup',
  curbside_pickup: 'pickup',
  drive_through_pickup: 'pickup',
  local_pickup: 'pickup',
  breadstack_canfleet_local_shipping: 'delivery',
  bsof_local_shipping: 'delivery',
  bs_homerun_local_shipping: 'delivery',
  bs_tpd_local_shipping: 'delivery',
  cantec_delivery: 'delivery',
  flat_rate: 'mail_order',
  free_shipping: 'mail_order',
  canada_post: 'mail_order',
  canada_post_shipping_by_nosites_left: 'mail_order',
}

export const resolveFulfillmentGroup = (
  fulfillmentType: string | null | undefined,
): OrderFulfillmentGroup =>
  (fulfillmentType && FULFILLMENT_GROUP[fulfillmentType]) || 'processing'

// An order is "failed" (critical error) when it could not be pushed to its POS.
// The status lives in one of three store-type fields; `failed_dismissed` means
// the alert was already dismissed and must NOT count as failed.
export const ORDER_CREATE_FAILED_STATUS = 'failed'

export const isOrderCreateFailed = (order: {
  create_to_cova_status?: string
  create_to_greenline_status?: string
  create_to_pos_status?: string
}): boolean =>
  order.create_to_cova_status === ORDER_CREATE_FAILED_STATUS ||
  order.create_to_greenline_status === ORDER_CREATE_FAILED_STATUS ||
  order.create_to_pos_status === ORDER_CREATE_FAILED_STATUS

export const ORDER_STATUS_TABS: OrderStatusFilter[] = [
  'all',
  'processing',
  'on-hold',
  'completed',
  'cancelled',
  'refunded',
  'pending',
  'failed',
]

export const ORDER_STATUS_LABELS: Record<string, string> = {
  all: 'All Statuses',
  pending: 'Pending',
  'on-hold': 'On Hold',
  processing: 'Processing',
  completed: 'Completed',
  cancelled: 'Cancelled',
  refunded: 'Refunded',
  failed: 'Failed',
  trash: 'Trash',
}

// An order can no longer be cancelled once it's reached a terminal or
// already-cancelled state.
export const ORDER_NOT_CANCELLABLE_STATUSES = [
  'completed',
  'refunded',
  'cancelled',
  'trash',
]

export const isOrderCancellable = (
  status: string | null | undefined,
): boolean => !!status && !ORDER_NOT_CANCELLABLE_STATUSES.includes(status)

// An order can be moved to trash while still active — once terminal
// (completed/refunded), already cancelled, or already trashed, delete is not
// offered.
export const ORDER_NOT_DELETABLE_STATUSES = [
  'completed',
  'refunded',
  'cancelled',
  'trash',
]

export const isOrderDeletable = (status: string | null | undefined): boolean =>
  !!status && !ORDER_NOT_DELETABLE_STATUSES.includes(status)

// Selectable fulfillment groups in the toolbar filter. `processing` is the
// fallback bucket (pre-fulfillment) and is not a user-facing filter option.
export const ORDER_FULFILLMENT_FILTERS: OrderFulfillmentGroup[] = [
  'delivery',
  'pickup',
  'mail_order',
]

export const ORDER_FULFILLMENT_LABELS: Record<OrderFulfillmentGroup, string> = {
  delivery: 'Delivery',
  pickup: 'Pickup',
  mail_order: 'Mail Order',
  processing: 'Processing',
}

// Reverse of FULFILLMENT_GROUP — the backend `fulfillment_type` filter is an
// Atlas Search `phrase` query whose `query` accepts an array of raw type
// strings (OR-match), so a group selection expands to every type it covers.
export const GROUP_TO_FULFILLMENT_TYPES: Record<
  OrderFulfillmentGroup,
  string[]
> = Object.entries(FULFILLMENT_GROUP).reduce(
  (acc, [type, group]) => {
    acc[group].push(type)
    return acc
  },
  {
    pickup: [],
    delivery: [],
    mail_order: [],
    processing: [],
  } as Record<OrderFulfillmentGroup, string[]>,
)

// Backend sort tokens accepted by `get_sort_v2` (orders.py). The keys here are
// the contract between the table headers / toolbar and the gateway.
export const ORDER_SORT_KEYS = {
  date: 'date_created_gmt',
  total: 'total',
} as const

export type OrderSortableColumn = keyof typeof ORDER_SORT_KEYS

const DAY_MS = 24 * 60 * 60 * 1000

// Convert a date-range preset into ISO `startDate`/`endDate` bounds relative to
// now. Pure apart from the single `new Date()` read, mirroring the mockup's
// Today / Last 7 / 30 / 90 Days options.
export const resolveDateRange = (
  preset: OrderDateRangePreset,
): { startDate?: string; endDate?: string } => {
  if (preset === 'all') return {}
  const now = new Date()
  const end = now.toISOString()
  if (preset === 'today') {
    const start = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
    ).toISOString()
    return { startDate: start, endDate: end }
  }
  const days = preset === '7d' ? 7 : preset === '30d' ? 30 : 90
  return {
    startDate: new Date(now.getTime() - days * DAY_MS).toISOString(),
    endDate: end,
  }
}

export const ORDER_DATE_RANGE_LABELS: Record<OrderDateRangePreset, string> = {
  all: 'All Dates',
  today: 'Today',
  '7d': 'Last 7 Days',
  '30d': 'Last 30 Days',
  '90d': 'Last 90 Days',
}

// ── POS orders (COVA / Greenline) ───────────────────────────────────────────
// The POS tab is gated to stores backed by a POS the per-store endpoints
// support. Only COVA and Greenline list endpoints exist today.
const POS_STORE_TYPES = new Set<string>(['cova', 'greenline'])

export const isPosStore = (storeType: string | null | undefined): boolean =>
  !!storeType && POS_STORE_TYPES.has(storeType.toLowerCase())

export const getPosSource = (
  storeType: string | null | undefined,
): PosOrderSource | undefined => {
  const normalized = storeType?.toLowerCase()
  if (normalized === 'cova') return 'cova'
  if (normalized === 'greenline') return 'greenline'
  return undefined
}

// Status taxonomy differs per POS, so options are keyed by source. `all` is the
// no-filter sentinel and is not sent to the backend.
export const POS_ORDER_STATUS_OPTIONS: Record<
  PosOrderSource,
  Array<{ value: string; label: string }>
> = {
  cova: [
    { value: 'all', label: 'All Statuses' },
    { value: 'Confirmed', label: 'Confirmed' },
    { value: 'Completed', label: 'Completed' },
    { value: 'Cancelled', label: 'Cancelled' },
  ],
  greenline: [
    { value: 'all', label: 'All Statuses' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'refunded', label: 'Refunded' },
  ],
}

export const POS_ORDER_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'all', label: 'All Types' },
  { value: 'pickup', label: 'Pickup' },
  { value: 'delivery', label: 'Delivery' },
  { value: 'instore', label: 'In Store' },
]

// NOTE: the POS list endpoints (`/cova/orders/`, `/greenline/stores/{id}/orders`)
// have no proven column-sort contract — the legacy POS tables are not
// header-sortable and rows arrive newest-first. We deliberately do not send
// sort params for the POS tab to avoid backend errors.
